import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { PrimaryButton, inputCls } from "@/components/shared/ui";
import { ErrorBox, StatusIcon } from "@/components/applicant/stageUi";
import { StepActions, StepTitle } from "@/components/applicant/QuestionStep";

const Eyebrow = ({ children }) => <h2 className="mb-3 mt-10 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">{children}</h2>;

const STATUS_TEXT = { pass: "Passed", warn: "Needs review", todo: "Not done yet" };

export const CheckList = ({ checks, testid = "review-checks" }) => (
  <ul className="divide-y rounded-2xl border bg-background" data-testid={testid}>
    {checks.map((c) => (
      <li key={c.key} className="flex items-start gap-3 p-3" data-testid={`review-check-${c.key}`} data-status={c.status}>
        <StatusIcon status={c.status} className="mt-px size-5" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={cn("text-sm", !c.cross_check && "font-medium")}>{c.label}</span>
            {c.status !== "pass" && <span className={cn("whitespace-nowrap text-xs font-medium", c.status === "warn" ? "text-warning" : "text-muted-foreground")} data-testid={`review-check-status-${c.key}`}>{STATUS_TEXT[c.status]}</span>}
          </div>
          {c.detail && <p className="mt-0.5 whitespace-pre-line text-xs text-muted-foreground">{c.detail}</p>}
          {c.note && <p className="mt-1.5 rounded-lg bg-panel p-2 text-sm" data-testid={`review-note-${c.key}`}><span className="font-medium">Your note: </span>{c.note}</p>}
        </div>
      </li>
    ))}
  </ul>
);

const STEP_STATUS_TEXT = { later: "Do this later", needs_fixing: "Needs fixing", checking: "Still checking", todo: "Not done yet" };

/** Steps that aren't done yet, each linking back to its own screen. */
const Outstanding = ({ steps, token }) => (
  <ul className="divide-y overflow-hidden rounded-2xl border bg-background" data-testid="review-outstanding">
    {steps.map((s) => (
      <li key={s.key}>
        <Link to={`/a/${token}/${s.key}`} className="flex items-center gap-3 p-3 hover:bg-panel" data-testid={`review-outstanding-${s.key}`} data-status={s.status}>
          <StatusIcon status="todo" className="size-5" />
          <span className="min-w-0 flex-1 text-sm font-medium">{s.title}</span>
          <span className="whitespace-nowrap text-xs font-medium text-muted-foreground">{STEP_STATUS_TEXT[s.status]}</span>
          <ChevronRight className="size-4 text-muted-foreground" />
        </Link>
      </li>
    ))}
  </ul>
);

/** The last step, on two screens: what's still to do and what's done, then the email, consent and Send. */
export const ReviewStage = ({ data, token, setData, part, onContinue, onNext }) => {
  const [consent, setConsent] = useState(false);
  const [email, setEmail] = useState(data.submission.email || data.contact?.email || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const warn = data.review.checks.some((c) => c.status === "warn");
  const ready = data.ready_to_submit;
  const outstanding = data.steps.filter((s) => s.kind !== "review" && !["done", "explained"].includes(s.status));
  const done = data.review.checks.filter((c) => c.status !== "todo");
  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      setData((await api.post(`/a/${token}/submit`, { consent, email: email.trim() })).data);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (part === "send") {
    return (
      <div className="rise" data-testid="stage-send">
        <StepTitle sub={`${data.let.agency_name} gets your results for ${data.let.address}.`}>Send your application</StepTitle>
        <div className="mt-8 flex flex-col gap-4">
          <label className="grid gap-2">
            <span className="text-sm font-medium">Your email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@example.com"
              className={cn(inputCls, "h-12 text-base")} data-testid="review-email-input" />
            <span className="text-sm text-muted-foreground">Only used if the agent needs you to fix something.</span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-2xl border bg-background p-4" data-testid="review-consent">
            <Checkbox checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5 size-5" data-testid="review-consent-checkbox" />
            <span className="text-sm leading-relaxed">I agree to share these results with {data.let.agency_name} for {data.let.address}, and confirm my documents are genuine.</span>
          </label>
          {!ready && <ErrorBox testid="review-not-ready">Finish the steps under "Still to do" first.</ErrorBox>}
          {error && <ErrorBox testid="review-error">{error}</ErrorBox>}
        </div>
        <StepActions>
          <PrimaryButton size="xl" className="w-full" onClick={submit} isLoading={busy} disabled={!consent || !validEmail || !ready} testid="review-submit-button">Send to {data.let.agency_name}</PrimaryButton>
        </StepActions>
      </div>
    );
  }

  return (
    <div className="rise" data-testid="stage-review">
      <StepTitle sub={outstanding.length ? "Finish these steps, then come back here to send." : warn ? `Some checks need ${data.let.agency_name}'s review, and your notes go with them.` : `Everything checked out. One last look, then send it to ${data.let.agency_name}.`}>
        {outstanding.length ? "Nearly ready to send" : "Final check"}
      </StepTitle>

      {outstanding.length > 0 && (
        <>
          <Eyebrow>Still to do</Eyebrow>
          <Outstanding steps={outstanding} token={token} />
        </>
      )}

      {done.length > 0 && (
        <>
          <Eyebrow>Done and checked</Eyebrow>
          <CheckList checks={done} />
        </>
      )}

      <StepActions>
        {outstanding.length ? (
          <>
            <p className="text-center text-sm text-muted-foreground" data-testid="review-not-ready">{outstanding.length === 1 ? "1 step" : `${outstanding.length} steps`} to go before you can send</p>
            <PrimaryButton size="xl" className="w-full" onClick={() => onNext(outstanding[0].key)} testid="review-next-outstanding">Do the next step <ArrowRight /></PrimaryButton>
          </>
        ) : (
          <PrimaryButton size="xl" className="w-full" onClick={onContinue} testid="review-continue-button">Continue <ArrowRight /></PrimaryButton>
        )}
      </StepActions>
    </div>
  );
};
